import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { disclosureStatement } from '#/content/disclosure-statement';
import type { DefinitionTerm } from '#/content/lib';

interface DefinitionDrawerContextValue {
  open: (slug: string) => void;
  close: () => void;
  activeDefinition: string | null;
  registeredTerms: string[];
  registerTerm: (slug: string) => void;
  unregisterTerm: (slug: string) => void;
  /** Page glossary and shared drawer content, keyed by slug. */
  termsBySlug: Map<string, DefinitionTerm>;
  /** Seed the page's glossary content; called by detail views once loaded. */
  setTerms: (terms: Map<string, DefinitionTerm>) => void;
  /**
   * A term's long description, shaped for the optional `description` props that
   * render it — an unauthored term reads as absent, not as an empty slot.
   */
  termDescription: (slug: string) => string | undefined;
}

const EMPTY_TERMS: Map<string, DefinitionTerm> = new Map();

const DefinitionDrawerContext =
  createContext<DefinitionDrawerContextValue | null>(null);

export function DefinitionDrawerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [activeDefinition, setActiveDefinition] = useState<string | null>(null);
  const [registeredTerms, setRegisteredTerms] = useState<string[]>([]);
  const [pageTerms, setTermsBySlug] =
    useState<Map<string, DefinitionTerm>>(EMPTY_TERMS);
  // Shared content survives page glossary updates without registering it in
  // "Other terms on this page".
  const termsBySlug = useMemo(
    () =>
      new Map([...pageTerms, [disclosureStatement.slug, disclosureStatement]]),
    [pageTerms],
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const registerTerm = useCallback((slug: string) => {
    setRegisteredTerms((prev) =>
      prev.includes(slug) ? prev : [...prev, slug],
    );
  }, []);

  const unregisterTerm = useCallback((slug: string) => {
    setRegisteredTerms((prev) => prev.filter((s) => s !== slug));
  }, []);

  // Write drawer state directly to the DOM so CSS can respond via
  // [data-drawer-open] descendant selectors — no layout component re-renders.
  // useLayoutEffect fires before paint so the attribute is set before the
  // browser composites the frame, giving the CSS transition a clean start.
  useLayoutEffect(() => {
    containerRef.current?.toggleAttribute(
      'data-drawer-open',
      activeDefinition !== null,
    );
  }, [activeDefinition]);

  const termDescription = useCallback(
    (slug: string) => termsBySlug.get(slug)?.description ?? undefined,
    [termsBySlug],
  );

  const value = useMemo(
    () => ({
      open: setActiveDefinition,
      close: () => setActiveDefinition(null),
      activeDefinition,
      registeredTerms,
      registerTerm,
      unregisterTerm,
      termsBySlug,
      setTerms: setTermsBySlug,
      termDescription,
    }),
    [
      activeDefinition,
      registeredTerms,
      registerTerm,
      unregisterTerm,
      termsBySlug,
      termDescription,
    ],
  );

  return (
    <DefinitionDrawerContext.Provider value={value}>
      {/* display:contents keeps this wrapper invisible to layout */}
      <div ref={containerRef} className="contents">
        {children}
      </div>
    </DefinitionDrawerContext.Provider>
  );
}

/**
 * Read the drawer state, and — when `definitions` is passed — publish them as
 * the page's glossary so tooltips, stat descriptions, and the drawer resolve by
 * slug. Exactly one component per page should pass them; the rest read.
 *
 * The map must be referentially stable across renders (the content hooks return
 * a cached one), or seeding re-runs on every render.
 */
export function useDefinitionDrawer(
  definitions?: Map<string, DefinitionTerm>,
): DefinitionDrawerContextValue {
  const ctx = useContext(DefinitionDrawerContext);
  if (!ctx) {
    throw new Error(
      'useDefinitionDrawer must be used within DefinitionDrawerProvider',
    );
  }

  const { setTerms } = ctx;
  useLayoutEffect(() => {
    if (definitions) {
      setTerms(definitions);
    }
  }, [definitions, setTerms]);

  return ctx;
}
